import { useMemo } from 'react';
import {
  isGroupingColumnNumeric, isGroupingColumnText,
} from '~/_shared/types/grouping/grouping';
import { type SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { useMapSettingsGroupingActiveGroupColumnsSelector } from '~/store/mapSettings/grouping/mapSettingsGrouping.selectors';
import { useSpreadsheetDataDataSelector } from '~/store/spreadsheetData/spreadsheetData.selectors';
import {
  DataType, Unfiltered,
} from '~/store/spreadsheetData/spreadsheetData.state';
import {
  getNumberColumnBucketId, getTextColumnGroupIndex,
} from './grouping.helpers';
import type { GroupInfo } from './useGetSpreadsheetRowIdsByGroupInfo.hook';

export const useGetGroupInfoBySpreadsheetRowId = (spreadsheetRowId?: SpreadsheetRowId, hierarchyIndicator?: number): GroupInfo | null => {
  const spreadsheetData = useSpreadsheetDataDataSelector();
  const activeGroupColumns = useMapSettingsGroupingActiveGroupColumnsSelector();

  const groupingColumn = useMemo(() => (
    activeGroupColumns?.[hierarchyIndicator || 0] || null
  ), [activeGroupColumns, hierarchyIndicator]);

  return useMemo(() => {
    if (!spreadsheetData || !groupingColumn || !spreadsheetRowId || groupingColumn.spreadsheetId !== spreadsheetRowId.spreadsheetId) {
      return null;
    }

    const spreadsheetId = groupingColumn.spreadsheetId;
    const columnId = groupingColumn.columnId;

    if (isGroupingColumnNumeric(groupingColumn)) {
      const bucket = getNumberColumnBucketId(spreadsheetData, spreadsheetId, columnId, spreadsheetRowId.rowId, groupingColumn);
      return {
        groupingColumn,
        bucketId: bucket.id,
      };
    }

    if (isGroupingColumnText(groupingColumn)) {
      const uniqueGroupId = getTextColumnGroupIndex(spreadsheetData, spreadsheetId, columnId, spreadsheetRowId.rowId);
      const groupData = uniqueGroupId !== null && spreadsheetData.values[spreadsheetId]?.[Unfiltered]?.[columnId]?.[DataType.GROUP]?.extra.uniqueGroups[uniqueGroupId]
        ? spreadsheetData.values[spreadsheetId]?.[Unfiltered]?.[columnId]?.[DataType.GROUP]?.extra.uniqueGroups[uniqueGroupId]
        : null;

      if (uniqueGroupId !== null && groupData) {
        return {
          groupingColumn,
          uniqueGroupIndex: uniqueGroupId,
          groupId: groupData.id,
        };
      }
    }
    return null;
  }, [groupingColumn, spreadsheetData, spreadsheetRowId]);
};
